import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import bg3 from "../assets/images/solar/bg3.webp"  // Smart & Green Real Estate
import bg2 from "../assets/images/solar/bg5.webp"  // E-Mobility Services
import bg1 from "../assets/images/solar/bg1.webp"  // Photovoltaic Solar Energy

import Navbar from "../components/navbar";
import HomeNav from "../components/homeNav";
import HomeProcess from "../components/homeProcess";
import About from "../components/about";
import Services from "../components/services";
import AgencyTab from "../components/agencyTab";
import Cta from "../components/cta";
import Blogs from "../components/blog";
import GetInTouch from "../components/getInTuoch";
import Footer from "../components/footer";
import Switcher from "../components/switcher";
import CmsSections from "../components/cmsSections";
import HtmlContent from "../components/HtmlContent";
import { blockData, mediaUrl, sectionBlocks, trackVisit, useSiteData } from "../lib/siteApi";

const fallbackHero = [
    {
        image: bg1,
        title: "Powering Rwanda with Clean Solar Energy",
        description: "Leading provider of photovoltaic solar solutions. From residential rooftops to large-scale commercial installations, we deliver reliable off-grid and on-grid solar systems that reduce costs and carbon footprint.",
        service: "Photovoltaic Solar Energy",
        icon: "☀️",
        to: "/services/category/photovoltaic",
    },
    {
        image: bg3,
        title: "Building Tomorrow's Green Communities",
        description: "Innovative smart and green real estate development in Rwanda. We design and construct eco-friendly buildings with integrated renewable energy, sustainable materials, and intelligent energy management systems.",
        service: "Smart & Green Real Estate",
        icon: "🏢",
        to: "/services/category/realestate",
    },
    {
        image: bg2,
        title: "Driving the Electric Revolution",
        description: "Comprehensive e-mobility solutions for Rwanda's sustainable transport future. EV charging infrastructure, fleet electrification, and complete electric vehicle services to accelerate the transition to clean transportation.",
        service: "E-Mobility Services",
        icon: "⚡",
        to: "/services/category/emobility",
    }
];

export default function Index(){
    const site = useSiteData();
    const [currentSlide, setCurrentSlide] = useState(0);
    const heroBlocks = sectionBlocks(site.content, "hero");
    const heroSlides = heroBlocks.length
        ? heroBlocks.map((block, index) => {
            const extra = blockData(block);
            const byKey = {
              "hero.solar": "/services/category/photovoltaic",
              "hero.realestate": "/services/category/realestate",
              "hero.emobility": "/services/category/emobility",
            };
            return {
                image: mediaUrl(block.imageUrl) || fallbackHero[index % fallbackHero.length].image,
                title: block.title || fallbackHero[index % fallbackHero.length].title,
                description: block.body || "",
                service: extra.service || block.key || "RENERG",
                icon: extra.icon || "☀️",
                to: extra.link || byKey[block.key] || fallbackHero[index % fallbackHero.length].to,
            };
        })
        : fallbackHero;

    useEffect(() => {
        trackVisit();
    }, []);

    useEffect(() => {
        if (!heroSlides.length) return undefined;
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 6000);
        return () => clearInterval(interval);
    }, [heroSlides.length]);

    useEffect(() => {
        if (currentSlide >= heroSlides.length) setCurrentSlide(0);
    }, [currentSlide, heroSlides.length]);

    const goToSlide = (index) => {
        setCurrentSlide(index);
    };

    return(
        <>
        <Navbar/>
        
        <section className="relative overflow-hidden sm:min-h-[100svh]" id="home">
            {heroSlides.map((slide, index) => (
                <Link
                    key={`${slide.title}-${index}-bg`}
                    to={slide.to || "/services"}
                    className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out ${
                        currentSlide === index ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
                    }`}
                    style={{ backgroundImage: `url(${slide.image})` }}
                    aria-label={slide.title}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/60"></div>
                </Link>
            ))}

            <div className="container relative z-20 flex items-center px-4 pb-16 pt-28 pointer-events-none sm:min-h-[100svh] sm:px-6 sm:pb-36 sm:pt-36 lg:px-8">
                <div className="mx-auto w-full max-w-4xl">
                    <div className="relative flex w-full items-center sm:min-h-[26rem]">
                        {heroSlides.map((slide, index) => (
                            <div
                                key={`${slide.title}-${index}-copy`}
                                className={`w-full max-w-4xl text-center lg:text-left space-y-4 sm:absolute sm:space-y-5 md:space-y-6 transition-all duration-1000 ease-in-out ${
                                    currentSlide === index 
                                        ? 'relative opacity-100 translate-y-0 pointer-events-auto' 
                                        : 'absolute h-0 overflow-hidden opacity-0 pointer-events-none sm:h-auto sm:overflow-visible sm:translate-y-8'
                                }`}
                            >
                                <Link to={slide.to || "/services"} className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 bg-teal-500/25 backdrop-blur-md border border-teal-400/40 rounded-full shadow-lg hover:bg-teal-500/40">
                                    <span className="text-xl sm:text-2xl">{slide.icon}</span>
                                    <span className="text-teal-300 font-semibold text-sm sm:text-base tracking-wide">
                                        {slide.service}
                                    </span>
                                </Link>

                                <h1 className="renerg-hero text-white">
                                    <Link to={slide.to || "/services"} className="hover:text-teal-200">{slide.title}</Link>
                                </h1>

                                <div className="renerg-body text-slate-100 max-w-3xl mx-auto lg:mx-0">
                                    <HtmlContent html={slide.description} imageTo={slide.to || "/services"} />
                                </div>

                                <div className="pt-4 sm:pt-6 flex flex-wrap gap-3 justify-center lg:justify-start">
                                    <Link 
                                        to={slide.to || "/services"}
                                        className="inline-flex items-center justify-center h-12 sm:h-14 px-8 sm:px-10 tracking-wide font-semibold rounded-xl bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white shadow-2xl shadow-teal-500/25 transition-all duration-300 hover:shadow-2xl hover:shadow-teal-500/40 hover:scale-105 text-base sm:text-lg"
                                    >
                                        Explore this service
                                        <i className="mdi mdi-chevron-right ms-2 text-xl"></i>
                                    </Link>
                                    <Link
                                        to="/contact"
                                        className="inline-flex items-center justify-center h-12 sm:h-14 px-8 font-semibold rounded-xl border border-white/40 bg-white/10 text-white backdrop-blur hover:bg-white/20"
                                    >
                                        Get a quote
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="relative z-30 mt-6 flex justify-center gap-3 pb-8 sm:absolute sm:bottom-28 sm:left-1/2 sm:mt-0 sm:-translate-x-1/2 sm:pb-0 sm:gap-4">
                {heroSlides.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`transition-all duration-500 ease-out rounded-full shadow-lg ${
                            currentSlide === index 
                                ? 'w-10 sm:w-14 h-3 bg-gradient-to-r from-teal-500 to-emerald-500 shadow-teal-500/50' 
                                : 'w-3 h-3 bg-white/50 hover:bg-white/80 hover:scale-110'
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                    />
                ))}
            </div>
        </section>

        <HomeNav />
        <About/>
        <HomeProcess />
        <AgencyTab/>
        <Services/>
        <Blogs/>
        <Cta/>
        <CmsSections content={site.content} />
        <GetInTouch/>
        <Footer/>
        <Switcher/>
        </>
    )
}
